
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pencil } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

interface BusinessNameEditorProps {
  userId: string;
  businessName: string | null;
}

export const BusinessNameEditor = ({ userId, businessName }: BusinessNameEditorProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newBusinessName, setNewBusinessName] = useState(businessName || "");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleUpdateBusinessName = async () => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          company: newBusinessName || null
        })
        .eq('id', userId);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['profile'] });
      setIsEditing(false);
      
      toast({
        title: "Business name updated",
        description: "Your business name has been successfully updated.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update business name. Please try again.",
        variant: "destructive",
      });
    }
  };

  const startEditing = () => {
    setNewBusinessName(businessName || "");
    setIsEditing(true);
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">Business Name</p>
        {!isEditing && (
          <Button
            variant="ghost"
            size="sm"
            onClick={startEditing}
            className="h-8 w-8 p-0"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      </div>
      {isEditing ? (
        <div className="space-y-2">
          <Input
            value={newBusinessName}
            onChange={(e) => setNewBusinessName(e.target.value)}
            placeholder="Enter business name"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleUpdateBusinessName}
              className="flex-1"
            >
              Save
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsEditing(false)}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500">
          {businessName || "Not set"}
        </p>
      )}
    </div>
  );
};
